import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFillSettingsModal from './AutoFillSettingsModal';

const CreateRefundConfigSection = ({ config, onChange }) => {
  const visibilityKeys = [
    { key: 'resolutionCenter', label: 'Resolution Center' },
    { key: 'createPaymentForm', label: 'Create Payment Form' },
    { key: 'approvalsPage', label: 'Approvals Page' },
    { key: 'refundsPage', label: 'Refunds Page' },
  ];

  const [lockedColumns, setLockedColumns] = useState([]);
  const [autoFillEditField, setAutoFillEditField] = useState(null);

  const nonLineItems = config.filter(
    (item) => !item.field.startsWith('lineItem.'),
  );
  const lineItems = config.filter((item) => item.field.startsWith('lineItem.'));

  const toggleColumnLock = (visibilityKey) => {
    setLockedColumns((prev) => {
      if (prev.includes(visibilityKey)) {
        return prev.filter((key) => key !== visibilityKey);
      } else {
        return [...prev, visibilityKey];
      }
    });
  };

  const handleToggleChange = (field, visibilityKey) => (event) => {
    const isChecked = event.target.checked;
    const updatedConfig = config.map((item) => {
      if (item.field === field) {
        const newVisibility = { ...item.isVisible };

        newVisibility[visibilityKey] = isChecked;

        if (lockedColumns.length > 1 && lockedColumns.includes(visibilityKey)) {
          lockedColumns.forEach((lockedKey) => {
            newVisibility[lockedKey] = isChecked;
          });
        }

        return {
          ...item,
          isVisible: newVisibility,
        };
      }
      return item;
    });
    onChange(updatedConfig);
  };

  const handleNameChange = (field, value) => {
    const updatedConfig = config.map((item) =>
      item.field === field ? { ...item, name: value } : item,
    );
    onChange(updatedConfig);
  };

  const handleSettingsSave = (field, { autoFill, formProperties }) => {
    const updatedConfig = config.map((item) => {
      if (item.field !== field) return item;
      const next = { ...item };
      if (autoFill === undefined) {
        delete next.autoFill;
      } else {
        next.autoFill = autoFill;
      }
      if (formProperties === undefined) {
        delete next.formProperties;
      } else {
        next.formProperties = formProperties;
      }
      return next;
    });
    onChange(updatedConfig);
  };

  const editingItem = config.find((i) => i.field === autoFillEditField);

  const renderTableRows = (items) => {
    return items.map((item) => (
      <TableRow
        key={item.field}
        sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
        <TableCell>{item.title}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size='small'
              value={item.name}
              onChange={(e) => handleNameChange(item.field, e.target.value)}
              fullWidth
              variant='outlined'
            />
            <Tooltip
              title={
                item.autoFill || item.formProperties?.isRequired
                  ? 'Edit field settings'
                  : 'Configure field settings'
              }>
              <IconButton
                size='small'
                onClick={() => setAutoFillEditField(item.field)}
                sx={{
                  color:
                    item.autoFill || item.formProperties?.isRequired
                      ? 'primary.main'
                      : 'grey.500',
                }}>
                <Badge
                  variant='dot'
                  invisible={!item.autoFill && !item.formProperties?.isRequired}
                  overlap='circular'
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  sx={{
                    '& .MuiBadge-badge': { backgroundColor: 'red' },
                  }}>
                  <SettingsIcon fontSize='small' />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        {visibilityKeys.map((vKey) => (
          <TableCell key={vKey.key} align='center'>
            <Checkbox
              checked={item.isVisible?.[vKey.key] || false}
              onChange={handleToggleChange(item.field, vKey.key)}
              color='primary'
            />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <Box sx={{ mb: 4, p: 3 }}>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='create-refund-config-content'
          id='create-refund-config-header'>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
              Payment Form Configuration
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Configure field visibility and labels across all payment forms.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                    Field
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                    Field Name
                  </TableCell>
                  {visibilityKeys.map((vKey) => (
                    <TableCell
                      key={vKey.key}
                      align='center'
                      sx={{ fontWeight: 'bold', width: '12.5%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}>
                        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                          {vKey.label}
                        </Typography>
                        <Tooltip
                          title={
                            lockedColumns.includes(vKey.key)
                              ? 'Locked - changes will sync with other locked columns'
                              : 'Unlocked - click to lock this column'
                          }>
                          <IconButton
                            size='small'
                            onClick={() => toggleColumnLock(vKey.key)}
                            sx={{
                              color: lockedColumns.includes(vKey.key)
                                ? 'primary.main'
                                : 'grey.400',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}>
                            {lockedColumns.includes(vKey.key) ? (
                              <LockIcon fontSize='small' />
                            ) : (
                              <LockOpenIcon fontSize='small' />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {nonLineItems.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell
                        colSpan={visibilityKeys.length + 2}
                        align='center'
                        sx={{
                          fontWeight: 'bold',
                          color: 'primary.main',
                          py: 1.5,
                        }}>
                        Payment Fields
                      </TableCell>
                    </TableRow>
                    {renderTableRows(nonLineItems)}
                  </>
                )}
                {lineItems.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell
                        colSpan={visibilityKeys.length + 2}
                        align='center'
                        sx={{
                          fontWeight: 'bold',
                          color: 'primary.main',
                          py: 1.5,
                        }}>
                        Line Item Fields
                      </TableCell>
                    </TableRow>
                    {renderTableRows(lineItems)}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
      <AutoFillSettingsModal
        open={Boolean(autoFillEditField)}
        onClose={() => setAutoFillEditField(null)}
        fieldTitle={editingItem?.title}
        value={editingItem?.autoFill}
        formProperties={editingItem?.formProperties}
        onSave={(payload) => {
          handleSettingsSave(autoFillEditField, payload);
          setAutoFillEditField(null);
        }}
      />
    </Box>
  );
};

export default CreateRefundConfigSection;
